import { type PowerSyncDrizzleDB, user } from "@malla/powersync";
import { patchRowVerified } from "../powersync/verified-write";

/**
 * ── Why ALL mutations below carry `.where(eq(user.id, userId))` ─
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this shape:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * meaning that, in guest mode, the `await` fell on the builder WITHOUT filter and
 * the UPDATE went out **without `WHERE`** → it rewrote ALL `user` rows in the
 * local database. The branch existed for symmetry with `getUserThemeSettingsQuery`,
 * which for the guest reads with `.limit(1)` and no filter; but a read `limit(1)`
 * and an unfiltered UPDATE are not the same bet: the former picks the wrong
 * row, while the latter crushes all of them.
 *
 * The premise behind it —"the guest database has only one `user`
 * row, so it doesn't matter"— does not hold: `user` is NOT `localOnly` in
 * `AppSchema`, so each touched row also enqueues its own upload op in
 * `ps_crud`.
 *
 * The correct filter is NOT guessed, it has been verified in this repo: the guest
 * row is seeded by `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) with
 * `db.insert(user).values({ id: uid, … })`, where `uid = useSession().userId()`;
 * and for a guest tab that `userId()` returns exactly `VISITOR_ID`
 * (`apps/web/src/lib/auth.ts`: `account.kind === "guest" ? VISITOR_ID : …`).
 * Thus `eq(user.id, userId)` points to the SAME row that the unfiltered branch
 * intended to touch when the table had only one row — identical behavior
 * in the normal case, bounded in the rest. It is also the form already used by
 * `updateUserFontFamilyMutation` and `updateUserImageMutation`, which never
 * had a guest branch.
 *
 * It slipped in because the block of `eslint.config.js` enabling
 * `drizzle/enforce-update-with-where` pointed with `files:` to `packages/db` and
 * `packages/api`, two packages that do NOT exist in this repo: the rule was
 * green for months without inspecting a single line.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 *
 * ── Why all 6 pass through `patchRowVerified` (audit 2026-08-13,
 * updated the same day after `ThemeProvider` seed guard) ─────
 *
 * The conversion to §4.11 (`333a9574`) excluded `user` from `patchRowVerified`
 * copying the argument from the canon: "the row is created by the server before
 * the client can write to it". Verified repo by repo (`git grep -n
 * '\.insert(user' -- '*.ts' '*.tsx' | grep -v __tests__` +
 * `apps/web/src/providers/theme-provider.tsx`): it is FALSE here as in the
 * other 10 repos that copied the same comment — and remains so even though
 * `ThemeProvider` is no longer part of the problem (see below):
 *
 *   - `db` (PowerSync/Drizzle accessor) becomes non-null as soon as the local
 *     SQLite is opened, not when initial sync finishes.
 *   - `userId()` is activated by the REST session (`signUpActor`/`signInActor`,
 *     `packages/shared/src/auth/controllers.ts`), a channel independent of the
 *     PowerSync stream.
 *
 * These two points suffice on their own: between `db`+`userId()` becoming
 * ready and the initial checkpoint bringing the `user` row created by the
 * server, there is a window in which a normal `UPDATE` (change language,
 * theme…) falls against a row the local view does not yet have — it matches ZERO
 * rows and silently returns "success". That window does not depend on anyone
 * seeding a local row: it is simply the view synchronizing.
 *
 * (Historical context, now CLOSED) Until today there was a third factor that
 * aggravated this same window for real accounts: the `createEffect` of
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) locally inserted
 * a 3-column "phantom" `user` row for ANY `uid`, without distinguishing guest from
 * real account, competing with the real row the server had already created
 * (`packages/core-rust/src/db/user.rs:101,350`) and that was on its way via sync.
 * The `uid !== VISITOR_ID` guard in that `createEffect` closed that concrete
 * root cause: the effect now only seeds for the guest. **This does NOT make the
 * protection of `patchRowVerified` over `user` unnecessary** — the two points
 * above (`db` and `userId()` ready before checkpoint) still open the same window
 * for any early write, with or without a phantom row involved.
 * `patchRowVerified` here is generic defense in depth for the checkpoint,
 * not a patch stuck to the already-closed bug — if one day it is removed,
 * let it be because it is proven that the window above no longer exists, not
 * because `ThemeProvider` stopped seeding phantoms.
 *
 * Measurement of the window: `packages/shared/src/powersync/__tests__/
 * verified-write.test.ts` (`user` block, with its CONTROL) and
 * `packages/shared/src/users/__tests__/theme-provider-seed-window.test.ts`
 * (already-closed root cause, kept as evidence).
 *
 * No need for `options.scope`: `user` has no organization or branch dimension
 * — `eq(user.id, userId)` is already the only meaningful predicate (one row
 * per user). The `extraWhere` of this module (see
 * `packages/shared/src/powersync/verified-write.ts`) is the equivalent piece
 * to `options.scope` from the canon (`listto-app@126dcb4e`) for the CC-12 case
 * (organization/branch); it simply does not apply to `user`.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale });
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId });
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme), not through REST:
 * the new photo appears instantly and survives a cold reload while offline. The
 * BINARY does go through REST (`POST /api/avatar`) because it does not fit in
 * the sync queue; only the key returned by that upload travels through here.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * A guest has no syncable row of its own and cannot have an avatar (zero rule),
 * so there is no `VISITOR_ID` branch here as there is for `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey });
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId });
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 });
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, {
    follow_device_theme: followDevice ? 1 : 0,
  });
}
